import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { AlertTriangle, CheckCircle2, Clock, Shield, Activity } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { fmtDate, fmtDateTime } from "../lib/datetime";
import { InstallBanner } from "../components/InstallPrompt";

const Tile = ({ tone, label, value, sub, target, testid, icon: Icon }) => (
  <div className={`kpi-tile kpi-tile-${tone}`} data-testid={testid}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      {Icon ? <Icon size={18} strokeWidth={2.5} style={{ color: tone === "critical" ? "#dc2626" : tone === "ok" ? "#059669" : "#f59e0b" }} /> : null}
    </div>
    <div className="font-chivo" style={{ fontSize: 40, fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 1.05, marginTop: 8, color: "#0f172a" }}>
      {value}
    </div>
    {sub ? <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>{sub}</div> : null}
    {target ? <div className="mono" style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>TARGET · {target}</div> : null}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [kpi, setKpi] = useState(null);
  const [structures, setStructures] = useState([]);
  const [due, setDue] = useState([]);
  const [redsCounts, setRedsCounts] = useState(null);

  useEffect(() => {
    api.get("/kpis/dashboard").then((r) => setKpi(r.data));
    api.get("/structures").then((r) => setStructures(r.data));
    api.get("/inspections/due").then((r) => setDue(r.data));
    api.get("/reds?scope=open").then((r) => setRedsCounts(r.data?.counts)).catch(() => {});
  }, []);

  const overdue = kpi?.headline_kpis?.missed_inspections_count ?? "—";
  const isMccain = user?.role === "mccain";

  return (
    <div data-testid="dashboard-page" style={{ padding: "24px 24px 64px" }}>
      <InstallBanner />
      <header style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 24, gap: 16, flexWrap: "wrap" }}>
        <div>
          <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>
            CONTRACT TRIAL DASHBOARD
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 900, margin: "4px 0 0" }}>McCain Whittlesey · Live KPIs</h1>
          <div style={{ color: "#64748b", marginTop: 4 }}>
            {isMccain ? "Read-only view shared with McCain — no commercial data." : "Internal operations console. Read-only McCain view is sandboxed."}
          </div>
        </div>
        <div className="mono" style={{ fontSize: 12, color: "#64748b" }} data-testid="kpi-computed-at">
          {kpi ? `Computed ${fmtDateTime(kpi.computed_at)}` : "Loading…"}
        </div>
      </header>

      {/* Reds banner — quick link to the action list when there are open items */}
      {redsCounts && (redsCounts.newell_open > 0 || redsCounts.mccain_open > 0 || redsCounts.in_progress > 0) ? (
        <Link to="/reds" data-testid="reds-dashboard-banner" style={{ display: "flex", padding: "12px 16px", marginBottom: 18, background: "#7f1d1d", color: "#fff", textDecoration: "none", alignItems: "center", justifyContent: "space-between", borderLeft: "5px solid #dc2626", borderRadius: 3, gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <AlertTriangle size={22} color="#fca5a5" />
            <div>
              <div className="mono" style={{ fontSize: 10, color: "#fca5a5", letterSpacing: "0.1em" }}>REDS &amp; ACTIONS</div>
              <div style={{ fontSize: 14, fontWeight: 800 }}>
                {redsCounts.newell_open} Newell · {redsCounts.mccain_open} McCain · {redsCounts.in_progress} in progress
              </div>
            </div>
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, opacity: 0.9 }}>OPEN LIST →</span>
        </Link>
      ) : null}

      {/* Zero RIDDOR banner */}
      <div data-testid="riddor-banner" style={{ background: "#0f172a", color: "white", padding: "16px 20px", borderRadius: 2, marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Shield size={28} strokeWidth={2.5} style={{ color: "#10b981" }} />
          <div>
            <div className="font-chivo" style={{ fontSize: 20, fontWeight: 900 }}>ZERO RIDDOR · ZERO LTA</div>
            <div className="mono" style={{ opacity: 0.7, fontSize: 12 }}>Site safety record · contract to date</div>
          </div>
        </div>
        <div className="mono" style={{ color: "#10b981", fontSize: 12, fontWeight: 600 }}>CLEAN</div>
      </div>

      {/* Headline KPIs (F-pattern: critical top-left) */}
      <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", margin: "0 0 12px" }}>
        Trial-deciding KPIs
      </h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 28 }}>
        <Tile
          tone={overdue === 0 ? "ok" : "critical"}
          label="Missed weekly inspections"
          value={overdue}
          sub={overdue === 0 ? "All standing structures on cycle" : "Action required"}
          target="0"
          testid="kpi-missed-inspections"
          icon={overdue === 0 ? CheckCircle2 : AlertTriangle}
        />
        <Tile tone="ok" label="RAMS within 4 hours" value={`${kpi?.headline_kpis?.rams_within_4h_pct ?? "—"}%`} sub="Stub — RAMS module Phase 1b" target="100%" testid="kpi-rams-4hr" icon={Clock} />
        <Tile tone="ok" label="Callout within 60 minutes" value={`${kpi?.headline_kpis?.callout_within_60min_pct ?? "—"}%`} sub="Stub — RRS webhook Phase 1c" target="100%" testid="kpi-callout-60min" icon={Activity} />
        <Tile tone="ok" label="Weekly meeting attendance" value={`${kpi?.headline_kpis?.meeting_attendance_pct ?? "—"}%`} sub="Stub — meeting log Phase 1d" target="100%" testid="kpi-meeting-attendance" icon={CheckCircle2} />
      </div>

      {/* Evidence tiles */}
      <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", margin: "0 0 12px" }}>
        Supporting evidence
      </h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 28 }}>
        <Tile tone="ok" label="Handover same-day" value={`${kpi?.evidence_tiles?.handover_same_day_pct ?? 0}%`} target="100%" testid="evidence-handover-sameday" />
        <Tile tone="ok" label="Inspection issued < 24h" value={`${kpi?.evidence_tiles?.inspection_issued_24h_pct ?? 0}%`} target="100%" testid="evidence-inspection-24h" />
        <Tile tone="ok" label="Standing structures" value={kpi?.evidence_tiles?.standing_structures_count ?? "—"} sub="Live temporary works register" testid="evidence-standing-count" />
        <Tile tone={kpi?.evidence_tiles?.audit_on_time_pct === 100 ? "ok" : "amber"} label="Weekly audit on time" value={`${kpi?.evidence_tiles?.audit_on_time_pct ?? 0}%`} sub="Stub — audit module Phase 1e" target="100%" testid="evidence-audit-ontime" />
      </div>

      {/* Overdue inspections call-out */}
      <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 24 }} data-testid="overdue-section">
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div className="font-chivo" style={{ fontWeight: 700, fontSize: 16 }}>Inspection queue</div>
            <div className="mono" style={{ fontSize: 12, color: "#64748b" }}>Overdue items pinned to top</div>
          </div>
          <Link to="/inspections" className="btn-ghost" data-testid="link-to-inspections">Open tracker →</Link>
        </div>
        <div style={{ maxHeight: 320, overflowY: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th>Structure</th>
                <th>Area</th>
                <th>Next due</th>
              </tr>
            </thead>
            <tbody>
              {due.slice(0, 8).map((row) => (
                <tr key={row.id} data-testid={`due-row-${row.id}`}>
                  <td>{row.overdue ? <span className="badge badge-red"><AlertTriangle size={12} /> Overdue</span> : <span className="badge badge-green">On cycle</span>}</td>
                  <td><Link to={`/structures/${row.id}`} style={{ color: "#0f172a", fontWeight: 600 }}>{row.description}</Link><div className="mono" style={{ color: "#94a3b8", fontSize: 11 }}>{row.id.slice(0, 8)} · {row.price_schedule_ref}</div></td>
                  <td>{row.area_name}</td>
                  <td className="mono">{row.next_due ? fmtDate(row.next_due) : "—"}</td>
                </tr>
              ))}
              {due.length === 0 ? <tr><td colSpan={4} style={{ textAlign: "center", color: "#94a3b8", padding: 24 }}>No standing structures yet</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mono" style={{ fontSize: 11, color: "#94a3b8" }}>
        {structures.length} structures registered · Phase 1 modules live: Structure Register · Handover · Inspections · KPI Dashboard
      </div>
    </div>
  );
}
